import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  SimpleGrid,
  Grid,
  GridItem,
  Text,
  VStack,
  HStack,
  Divider,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  RadioGroup,
  Radio,
  Checkbox,
  Textarea,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  MdLocalHospital,
  MdMonitorHeart,
  MdOutlineWarningAmber,
  MdExpandMore,
  MdSearch,
  MdLocationOn,
  MdAdd,
  MdPhoneForwarded,
} from "react-icons/md";
import AddressAutocomplete from "components/AddressAutocomplete";
import { useGoogleMaps } from "components/GoogleMapsLoader";
import apiClient from "../../../api/client";
import { BASE_URL } from "config/api";
import { getAllPatients } from "../../../api/api";
import AffectationMap from "../missions/components/AffectationMap";
import { fetchVehicleLocation as fetchVehicleLocationSecure } from "../../../api/external";

const missionFieldProps = {
  h: "42px",
  borderRadius: "6px",
  border: "1px solid",
  borderColor: "#E5E5E5",
  boxShadow: "0px 2px 3px 0px rgba(0,0,0,0.04)",
  fontSize: "13px",
  bg: "white",
};

const MEDECIN_SPECIALITE_OPTIONS = [
  "Médecine générale",
  "Urgences",
  "Réanimation",
  "Anesthésie-réanimation",
  "Cardiologie",
  "Pneumologie",
  "Neurologie",
  "Infectiologie",
  "Néphrologie",
  "Gastro-entérologie",
  "Endocrinologie",
  "Hématologie",
  "Oncologie",
  "Chirurgie générale",
  "Chirurgie viscérale",
  "Chirurgie orthopédique",
  "Pédiatrie",
  "Gynécologie-obstétrique",
  "Psychiatrie",
  "ORL",
  "Ophtalmologie",
  "Rhumatologie",
  "Médecine interne",
  "Gériatrie",
  "Dermatologie",
  "Autre",
];

const DoctorSearchDropdown = ({ value, onChange, options, placeholder }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchQuery, setSearchQuery] = useState("");

  const filteredDoctors = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return options;
    return options.filter((doctor) => {
      const fullName = `${doctor.nom || ""} ${doctor.prenom || ""}`.toLowerCase();
      return fullName.includes(q);
    });
  }, [options, searchQuery]);

  const selectedDoctor = options.find((doctor) => String(doctor.id) === String(value));

  return (
    <Box position="relative" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
      <Menu isOpen={isOpen} onClose={onClose} closeOnBlur={false}>
        <MenuButton
          as={Button}
          onClick={onOpen}
          w="100%"
          h="42px"
          justifyContent="flex-start"
          fontSize="13px"
          fontWeight="400"
          border="1px solid"
          borderRadius="6px"
          borderColor="#E5E5E5"
          boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
          bg="white"
          _hover={{ bg: "white" }}
          _active={{ bg: "white" }}
          textAlign="left"
          px="12px"
        >
          {selectedDoctor ? `${selectedDoctor.nom || ""} ${selectedDoctor.prenom || ""}`.trim() : placeholder}
        </MenuButton>
        <MenuList
          p="10px"
          minW="100%"
          maxH="320px"
          overflowY="auto"
          borderRadius="6px"
          boxShadow="0px 0px 20px 0px rgba(0,0,0,0.1)"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <InputGroup size="sm" mb="8px">
            <InputLeftElement pointerEvents="none">
              <Icon as={MdSearch} color="gray.400" />
            </InputLeftElement>
            <Input
              pl="34px"
              placeholder="Recherche médecin..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              {...missionFieldProps}
            />
          </InputGroup>
          {filteredDoctors.length === 0 ? (
            <Box px="10px" py="6px">
              <Text fontSize="12px" color="gray.500">Aucun médecin trouvé</Text>
            </Box>
          ) : (
            filteredDoctors.map((doctor) => (
              <MenuItem
                key={doctor.id}
                borderRadius="6px"
                mb="4px"
                p="14px 20px"
                onClick={() => {
                  onChange(doctor.id);
                  onClose();
                  setSearchQuery("");
                }}
              >
                <Text fontSize="12px">{`${doctor.nom || ""} ${doctor.prenom || ""}`.trim()}</Text>
              </MenuItem>
            ))
          )}
        </MenuList>
      </Menu>
    </Box>
  );
};

const VilleSearchDropdown = ({ value, onChange, options, placeholder }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Box position="relative">
      <Menu isOpen={isOpen} onClose={onClose}>
        <MenuButton
          as={Button}
          onClick={onOpen}
          w="100%"
          h="42px"
          justifyContent="flex-start"
          fontSize="13px"
          fontWeight="400"
          border="1px solid"
          borderRadius="6px"
          borderColor="#E5E5E5"
          boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
          bg="white"
          _hover={{ bg: "white" }}
          _active={{ bg: "white" }}
          textAlign="left"
          px="12px"
        >
          {value || placeholder}
        </MenuButton>
        <MenuList p="10px" maxH="320px" overflowY="auto" borderRadius="6px" boxShadow="0px 0px 20px 0px rgba(0,0,0,0.1)">
          {options.map((ville) => (
            <MenuItem
              key={ville}
              borderRadius="6px"
              mb="4px"
              p="14px 20px"
              bg={value === ville ? "#EFF6FF" : "white"}
              border="1px solid"
              borderColor={value === ville ? "rgba(69,156,233,0.2)" : "transparent"}
              onClick={() => {
                onChange(ville);
                onClose();
              }}
            >
              <Text fontSize="13px" color="#455879">{ville}</Text>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

const HospitalSearchDropdown = ({ value, onChange, options, placeholder }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const selectedHospital = options.find((hospital) => String(hospital.id) === String(value));

  return (
    <Box position="relative">
      <Menu isOpen={isOpen} onClose={onClose}>
        <MenuButton
          as={Button}
          onClick={onOpen}
          w="100%"
          h="42px"
          justifyContent="flex-start"
          fontSize="13px"
          fontWeight="400"
          border="1px solid"
          borderRadius="6px"
          borderColor="#E5E5E5"
          boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
          bg="white"
          _hover={{ bg: "white" }}
          _active={{ bg: "white" }}
          textAlign="left"
          px="12px"
        >
          {selectedHospital ? selectedHospital.nom : placeholder}
        </MenuButton>
        <MenuList p="10px" maxH="320px" overflowY="auto" borderRadius="6px" boxShadow="0px 0px 20px 0px rgba(0,0,0,0.1)">
          {options.map((hospital) => (
            <MenuItem
              key={hospital.id}
              borderRadius="6px"
              mb="4px"
              p="14px 20px"
              bg={String(value) === String(hospital.id) ? "#EFF6FF" : "white"}
              border="1px solid"
              borderColor={String(value) === String(hospital.id) ? "rgba(69,156,233,0.2)" : "transparent"}
              onClick={() => {
                onChange(hospital.id);
                onClose();
              }}
            >
              <Text fontSize="13px" color="#455879">{hospital.nom}</Text>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

export default function DossierRegulationCreate() {
  const navigate = useNavigate();
  const { loaded: googleMapsLoaded } = useGoogleMaps();
  const toast = useToast();
  const [patients, setPatients] = useState([]);
  const [hospitalVillesList, setHospitalVillesList] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [medecinsHopitalMission, setMedecinsHopitalMission] = useState([]);
  const [medecinsHopitalDestination, setMedecinsHopitalDestination] = useState([]);
  const [isAddDoctorModalOpen, setIsAddDoctorModalOpen] = useState(false);
  const [addDoctorContext, setAddDoctorContext] = useState(null);
  const [creatingDoctor, setCreatingDoctor] = useState(false);
  const [isSubmittingDossier, setIsSubmittingDossier] = useState(false);
  const [isAffectationModalOpen, setIsAffectationModalOpen] = useState(false);
  const [assigningMission, setAssigningMission] = useState(false);
  const [createdUrgentDossier, setCreatedUrgentDossier] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [allAmbulanciers, setAllAmbulanciers] = useState([]);
  const [selectedAmbulanceId, setSelectedAmbulanceId] = useState("");
  const [primaryLocation, setPrimaryLocation] = useState(null);
  const [newDoctorData, setNewDoctorData] = useState({
    nom: "",
    prenom: "",
    téléphone: "",
    ville: "",
    specialite: "",
  });
  const [form, setForm] = useState({
    transportType: "secondaire",
    nom: "",
    prenom: "",
    ipp: "",
    telephonePatient: "",
    age: "",
    sexe: "homme",
    nomAppelant: "",
    relationAppelant: "",
    appelantEstPatient: false,
    telephoneAppelant: "",
    motif: "",
    circonstances: "",
    gestes: "",
    danger: "",
    victimes: "1",
    tension: "120/80",
    frequenceCardiaque: "75",
    spo2: "98",
    glasgow: "15",
    autresConstantes: "",
    respire: "",
    conscient: "",
    priorite: "",
    notesTriage: "",
    villeDemandeur: "",
    hopitalDemandeur: "",
    serviceDemandeur: "",
    medecinDemandeur: "",
    villeDestination: "",
    hopitalDestination: "",
    serviceDestination: "",
    medecinReceveur: "",
    accordDestination: "",
    observationBreveTransfert: "",
    villeMissionPrimaire: "",
    adresseOriginePrimaire: "",
    adresseDestinationPrimaire: "",
  });
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const normalizeSexeForDisplay = (sexe) => {
    if (!sexe || typeof sexe !== "string") return "";
    const s = sexe.trim().toLowerCase();
    if (s === "m" || s === "male" || s === "homme" || s === "h") return "homme";
    if (s === "f" || s === "female" || s === "femme") return "femme";
    return sexe;
  };

  const getMedecinTelephone = (doctor) => {
    if (!doctor || typeof doctor !== "object") return "";
    const u = doctor.user;
    if (u && typeof u === "object") {
      const nested = u.téléphone ?? u.telephone;
      if (nested != null && String(nested).trim() !== "") return String(nested).trim();
    }
    const root = doctor.téléphone ?? doctor.telephone;
    return root == null ? "" : String(root).trim();
  };

  const selectMedecinReceveur = (doctorId) => {
    handleChange("medecinReceveur", doctorId);
  };

  const handleIppChange = (value) => {
    setForm((prev) => {
      const next = { ...prev, ipp: value };
      const match = patients.find(
        (p) => String(p.ipp ?? "").trim() === String(value ?? "").trim()
      );

      if (match) {
        next.nom = match.nom ?? "";
        next.prenom = match.prenom ?? "";
        next.telephonePatient = match.téléphone ?? match.telephone ?? "";
        next.age = match.age != null ? String(match.age) : "";
        next.sexe = normalizeSexeForDisplay(match.sexe) || prev.sexe;
      } else {
        // Si aucun IPP exact n'est trouvé, ne pas garder les données du patient précédent
        next.nom = "";
        next.prenom = "";
        next.telephonePatient = "";
        next.age = "";
        next.sexe = "homme";
      }

      return next;
    });
  };

  const handleIncrementField = (field, delta, min = 0) => {
    setForm((prev) => {
      const current = Number(prev[field] || 0);
      const next = Math.max(min, current + delta);
      return { ...prev, [field]: String(next) };
    });
  };

  const handleIncrementTension = (delta) => {
    setForm((prev) => {
      const [sysRaw, diaRaw] = String(prev.tension || "120/80").split("/");
      const systolique = Number(sysRaw) || 120;
      const diastolique = Number(diaRaw) || 80;
      const nextSystolique = Math.max(0, systolique + delta);
      const nextDiastolique = Math.max(0, diastolique + delta);
      return { ...prev, tension: `${nextSystolique}/${nextDiastolique}` };
    });
  };

  const toNull = (value) => {
    if (value === undefined || value === null) return null;
    if (typeof value === "string" && value.trim() === "") return null;
    return value;
  };

  const toOuiNonInt = (value) => {
    if (value === "oui") return 1;
    if (value === "non") return 0;
    return null;
  };

  const toNullableInt = (value) => {
    if (value === undefined || value === null) return null;
    const raw = String(value).trim();
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const getNowIsoString = () => new Date().toISOString();

  const parseLoc = (value) => {
    if (!value) return null;
    if (typeof value === "object" && value.lat != null && value.lng != null) {
      const lat = Number(value.lat);
      const lng = Number(value.lng);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
      return null;
    }
    const raw = String(value);
    const [latStr, lngStr] = raw.split(",");
    const lat = Number(latStr);
    const lng = Number(lngStr);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  };

  const missionLocation = useMemo(
    () => parseLoc(createdUrgentDossier?.loc) || parseLoc(createdUrgentDossier?.loc_dest),
    [createdUrgentDossier]
  );

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => String(v.id) === String(selectedAmbulanceId)) || null,
    [vehicles, selectedAmbulanceId]
  );

  const fetchVehicleLocation = async (trackerId) => {
    if (!trackerId) return null;
    try {
      return await fetchVehicleLocationSecure(trackerId);
    } catch (e) {
      return null;
    }
  };

  const openAffectationModal = async (dossierData) => {
    setCreatedUrgentDossier(dossierData || null);
    setSelectedAmbulanceId("");
    setIsAffectationModalOpen(true);
    try {
      const [vehiclesRes, usersRes] = await Promise.all([
        apiClient.get(`${BASE_URL}/vehicules`),
        apiClient.get(`${BASE_URL}/users`),
      ]);
      const vehiclesList = Array.isArray(vehiclesRes.data) ? vehiclesRes.data : [];
      const usersList = Array.isArray(usersRes.data) ? usersRes.data : [];
      const ambulanciersList = usersList.filter((u) => u.rôle === "ambulancier");
      const ambulancierIds = new Set(
        ambulanciersList
          .map((u) => Number(u.id))
          .filter((id) => !Number.isNaN(id) && id > 0)
      );

      const hasValidAmbulancier = (vehicle) => {
        const raw = vehicle?.user_id ?? vehicle?.ambulancier_id;
        if (raw === null || raw === undefined || raw === "") return false;
        const idNum = Number(raw);
        if (Number.isNaN(idNum) || idNum <= 0) return false;
        return ambulancierIds.has(idNum);
      };

      const isVehicleAvailable = (vehicle) => {
        const rawStatus =
          vehicle?.statut ??
          vehicle?.status ??
          vehicle?.etat ??
          vehicle?.disponibilité ??
          vehicle?.disponibilite ??
          vehicle?.état;
        if (rawStatus === null || rawStatus === undefined || rawStatus === "") return false;
        const normalized = String(rawStatus).trim().toLowerCase();
        return normalized.includes("disponible") || normalized.includes("available");
      };

      setVehicles(vehiclesList.filter((v) => hasValidAmbulancier(v) && isVehicleAvailable(v)));
      setAllAmbulanciers(ambulanciersList);
    } catch (e) {
      setVehicles([]);
      setAllAmbulanciers([]);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données d'affectation.",
        status: "error",
        isClosable: true,
      });
    }
  };

  const closeAffectationModal = () => {
    setAssigningMission(false);
    setSelectedAmbulanceId("");
    setCreatedUrgentDossier(null);
    setIsAffectationModalOpen(false);
  };

  const handleCreateMissionFromCreatedDossier = async () => {
    if (!createdUrgentDossier?.id) return;
    if (!selectedVehicle?.id || !selectedVehicle?.user_id) {
      toast({
        title: "Sélection requise",
        description: "Veuillez sélectionner une ambulance avec ambulancier assigné.",
        status: "warning",
        isClosable: true,
      });
      return;
    }
    try {
      setAssigningMission(true);
      await apiClient.post(`${BASE_URL}/dossier/${createdUrgentDossier.id}/creemission`, {
        ambulance_id: Number(selectedVehicle.id),
        ambulancier_id: Number(selectedVehicle.user_id),
      });
      toast({
        title: "Mission créée",
        description: "La mission a été créée avec succès.",
        status: "success",
        isClosable: true,
      });
      closeAffectationModal();
      navigate("/admin/dossier-regulation");
    } catch (e) {
      toast({
        title: "Erreur",
        description: "Impossible de créer la mission.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setAssigningMission(false);
    }
  };

  const resolvePatientId = async () => {
    const ippTrim = String(form.ipp || "").trim();
    const existingPatient = ippTrim
      ? patients.find((p) => String(p.ipp ?? "").trim() === ippTrim)
      : null;

    if (existingPatient?.id) {
      return existingPatient.id;
    }

    const createPayload = {
      ipp: toNull(ippTrim),
      nom: toNull(form.nom),
      prenom: toNull(form.prenom),
      age: toNullableInt(form.age),
      sexe: toNull(form.sexe),
      téléphone: toNull(form.telephonePatient),
    };

    const res = await apiClient.post(`${BASE_URL}/patients`, createPayload);
    return res?.data?.id || null;
  };

  const handleCreateDossier = async () => {
    if (isSubmittingDossier) return;
    try {
      setIsSubmittingDossier(true);
      const isSecondaire = form.transportType === "secondaire";

      if (!String(form.priorite || "").trim()) {
        toast({
          title: "Champ requis",
          description: "Le niveau d'urgence est obligatoire.",
          status: "warning",
          isClosable: true,
        });
        return;
      }

      if (!String(form.notesTriage || "").trim()) {
        toast({
          title: "Champ requis",
          description: "La note complémentaire est obligatoire.",
          status: "warning",
          isClosable: true,
        });
        return;
      }

      if (!isSecondaire) {
        if (!String(form.nom || "").trim()) {
          toast({
            title: "Champ requis",
            description: "Le nom du patient est obligatoire pour un transport primaire.",
            status: "warning",
            isClosable: true,
          });
          return;
        }
        if (!String(form.prenom || "").trim()) {
          toast({
            title: "Champ requis",
            description: "Le prénom du patient est obligatoire pour un transport primaire.",
            status: "warning",
            isClosable: true,
          });
          return;
        }
        if (!String(form.telephonePatient || "").trim()) {
          toast({
            title: "Champ requis",
            description: "Le téléphone patient est obligatoire pour un transport primaire.",
            status: "warning",
            isClosable: true,
          });
          return;
        }
      }
      if (isSecondaire) {
        if (!String(form.ipp || "").trim()) {
          toast({
            title: "Champ requis",
            description: "L'IPP est obligatoire pour un transport secondaire.",
            status: "warning",
            isClosable: true,
          });
          return;
        }
        if (!String(form.nom || "").trim()) {
          toast({
            title: "Champ requis",
            description: "Le nom du patient est obligatoire pour un transport secondaire.",
            status: "warning",
            isClosable: true,
          });
          return;
        }
        if (!String(form.prenom || "").trim()) {
          toast({
            title: "Champ requis",
            description: "Le prénom du patient est obligatoire pour un transport secondaire.",
            status: "warning",
            isClosable: true,
          });
          return;
        }
      }

      if (form.accordDestination === "oui" && !String(form.medecinReceveur || "").trim()) {
        toast({
          title: "Champ requis",
          description: "Le médecin receveur est obligatoire lorsque l'accord destination est obtenu.",
          status: "warning",
          isClosable: true,
        });
        return;
      }

      const patientId = await resolvePatientId();

      const hospitalMission = hospitals.find(
        (h) => String(h.id) === String(form.hopitalDemandeur)
      );
      const hospitalDestination = hospitals.find(
        (h) => String(h.id) === String(form.hopitalDestination)
      );

      const locPrimaire = primaryLocation
        ? `${primaryLocation.lat},${primaryLocation.lng}`
        : null;
      const isCriticalPriority = ["u0", "u1"].includes(String(form.priorite || "").toLowerCase());

      const receveurDoc = medecinsHopitalDestination.find(
        (d) => String(d.id) === String(form.medecinReceveur)
      );
      const receveurTel = getMedecinTelephone(receveurDoc);

      const payload = {
        patient_id: patientId,
        etat_mission: isCriticalPriority ? "urgente" : null,
        statut: isCriticalPriority ? "mission creee" : "attente medecin",
        date_mission: isCriticalPriority ? getNowIsoString() : null,
        urgence: isCriticalPriority ? 1 : null,
        type_mission: null,
        cause: toNull(form.priorite),
        diagnostic: null,
        motif: toNull(form.motif),
        adresse: isSecondaire ? toNull(hospitalMission?.nom) : toNull(form.adresseOriginePrimaire),
        adresse_destination: toNull(hospitalDestination?.nom),
        phone: toNull(form.telephonePatient),
        ville: isSecondaire ? toNull(form.villeDemandeur) : toNull(form.villeMissionPrimaire),
        ville_dest: toNull(form.villeDestination),
        hospital_id: toNull(form.hopitalDemandeur ? Number(form.hopitalDemandeur) : null),
        hospital_destination_id: toNull(form.hopitalDestination ? Number(form.hopitalDestination) : null),
        loc: isSecondaire ? toNull(hospitalMission?.loc) : toNull(locPrimaire),
        loc_dest: toNull(hospitalDestination?.loc),
        timer: null,
        doc_a: toNull(form.medecinDemandeur ? Number(form.medecinDemandeur) : null),
        doc_b:
          form.accordDestination === "oui" && form.medecinReceveur
            ? toNull(Number(form.medecinReceveur))
            : null,
        service_a: toNull(form.serviceDemandeur),
        service_b: toNull(form.serviceDestination),
        ta: toNull(form.tension),
        fc: toNull(form.frequenceCardiaque),
        spo2: toNull(form.spo2),
        autre: toNull(form.autresConstantes),
        glasgow: toNull(form.glasgow),
        transportType: toNull(form.transportType),
        nomAppelant: isSecondaire ? null : toNull(form.nomAppelant),
        relationAppelant: isSecondaire ? null : toNull(form.relationAppelant),
        appelantEstPatient: isSecondaire ? null : form.appelantEstPatient ? 1 : 0,
        telephoneAppelant: isSecondaire ? null : toNull(form.telephoneAppelant),
        circonstances: isSecondaire ? null : toNull(form.circonstances),
        observation: toNull(form.observationBreveTransfert),
        gestes: toNull(form.gestes),
        danger: toNull(form.danger),
        victimes: toNull(form.victimes ? Number(form.victimes) : null),
        respire: isSecondaire ? null : toOuiNonInt(form.respire),
        conscient: isSecondaire ? null : toOuiNonInt(form.conscient),
        note: toNull(form.notesTriage),
        accord: toOuiNonInt(form.accordDestination),
        remarque: toNull(receveurTel ? `Tél. médecin receveur: ${receveurTel}` : null),
      };

      const createdRes = await apiClient.post(`${BASE_URL}/dossier`, payload);
      let createdDossier = createdRes?.data || null;

      if (createdDossier?.id) {
        try {
          const freshDossierRes = await apiClient.get(`${BASE_URL}/dossier/${createdDossier.id}`);
          const freshDossier = freshDossierRes?.data || null;
          if (freshDossier) {
            createdDossier = freshDossier;
          }
        } catch (e) {
          // fallback on response payload below
        }
      }

      if (createdDossier && !createdDossier.loc && payload.loc) {
        createdDossier.loc = payload.loc;
      }
      if (createdDossier && !createdDossier.loc_dest && payload.loc_dest) {
        createdDossier.loc_dest = payload.loc_dest;
      }

      toast({
        title: "Dossier créé",
        status: "success",
        isClosable: true,
      });

      if (isCriticalPriority && createdDossier?.id) {
        await openAffectationModal(createdDossier);
      } else {
        navigate("/admin/dossier-regulation");
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de créer le dossier.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setIsSubmittingDossier(false);
    }
  };

  const isCriticalPriority = ["u0", "u1"].includes(String(form.priorite || "").toLowerCase());

  const fetchHopitaux = async () => {
    try {
      const res = await apiClient.get(`${BASE_URL}/hopitals`);
      setHospitals(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setHospitals([]);
    }
  };

  const fetchHospitalVilles = async () => {
    try {
      const res = await apiClient.get(`${BASE_URL}/hopitals/villes`);
      const data = res.data;
      const list = Array.isArray(data) ? data : data?.villes || [];
      setHospitalVillesList(Array.isArray(list) ? list : []);
    } catch (e) {
      setHospitalVillesList([]);
    }
  };

  const fetchMedecinsByHopital = async (hospitalId) => {
    if (!hospitalId) return [];
    try {
      const res = await apiClient.get(`${BASE_URL}/users/hopital/${hospitalId}/medecins`);
      return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
      return [];
    }
  };

  useEffect(() => {
    fetchHopitaux();
    fetchHospitalVilles();
  }, []);

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const res = await getAllPatients();
        const data = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
            ? res.data
            : [];
        setPatients(data);
      } catch (e) {
        setPatients([]);
      }
    };
    fetchPatients();
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!form.hopitalDemandeur) {
      setMedecinsHopitalMission([]);
      return;
    }
    fetchMedecinsByHopital(form.hopitalDemandeur).then((list) => {
      if (!cancelled) setMedecinsHopitalMission(list);
    });
    return () => {
      cancelled = true;
    };
  }, [form.hopitalDemandeur]);

  useEffect(() => {
    let cancelled = false;
    if (!form.hopitalDestination) {
      setMedecinsHopitalDestination([]);
      return;
    }
    fetchMedecinsByHopital(form.hopitalDestination).then((list) => {
      if (!cancelled) setMedecinsHopitalDestination(list);
    });
    return () => {
      cancelled = true;
    };
  }, [form.hopitalDestination]);

  useEffect(() => {
    if (form.transportType === "secondaire") return;

    const respireNon = form.respire === "non";
    const conscientNon = form.conscient === "non";
    const respireOui = form.respire === "oui";
    const conscientOui = form.conscient === "oui";
    const respireUnset = !form.respire;
    const conscientUnset = !form.conscient;

    if (respireUnset && conscientUnset) {
      setForm((prev) => ({ ...prev, priorite: "" }));
      return;
    }

    if (respireOui && conscientOui) {
      setForm((prev) => ({ ...prev, priorite: "" }));
      return;
    }

    if (respireNon && conscientNon) {
      setForm((prev) => ({ ...prev, priorite: "u0" }));
      return;
    }

    if (respireNon || conscientNon) {
      setForm((prev) => ({ ...prev, priorite: "u1" }));
    }
  }, [form.respire, form.conscient, form.transportType]);

  const medecinReceveurTelephoneLu = useMemo(() => {
    const doc = medecinsHopitalDestination.find((d) => String(d.id) === String(form.medecinReceveur));
    return getMedecinTelephone(doc);
  }, [form.medecinReceveur, medecinsHopitalDestination]);

  const openAddDoctorModal = (context) => {
    setAddDoctorContext(context);
    setNewDoctorData({
      nom: "",
      prenom: "",
      téléphone: "",
      ville: context === "demandeur" ? form.villeDemandeur : form.villeDestination,
      specialite: "",
    });
    setIsAddDoctorModalOpen(true);
  };

  const handleCreateDoctor = async () => {
    const targetHospitalId =
      addDoctorContext === "demandeur" ? form.hopitalDemandeur : form.hopitalDestination;

    if (!targetHospitalId) {
      toast({
        title: "Hôpital requis",
        description: "Sélectionnez d'abord un hôpital.",
        status: "error",
        isClosable: true,
      });
      return;
    }

    if (!newDoctorData.nom.trim() || !newDoctorData.prenom.trim()) {
      toast({
        title: "Champs obligatoires manquants",
        description: "Nom et prénom sont obligatoires.",
        status: "error",
        isClosable: true,
      });
      return;
    }

    try {
      setCreatingDoctor(true);
      const baseEmail = `${newDoctorData.nom}${newDoctorData.prenom}`
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "")
        .toLowerCase();

      const payload = {
        nom: newDoctorData.nom.trim(),
        prenom: newDoctorData.prenom.trim(),
        téléphone: newDoctorData.téléphone || null,
        ville: newDoctorData.ville || null,
        specialty: String(newDoctorData.specialite || "").trim() || null,
        email: `${baseEmail || "medecin"}@rsk.ma`,
        mot_de_passe: "medecin@rsk2026",
        rôle: "médecin",
        statut: "actif",
        langue: "fr",
        hospital_id: Number(targetHospitalId),
      };

      const res = await apiClient.post(`${BASE_URL}/users`, payload);
      const createdDoctor = res.data;

      if (addDoctorContext === "demandeur" && form.hopitalDemandeur) {
        const updated = await fetchMedecinsByHopital(form.hopitalDemandeur);
        setMedecinsHopitalMission(updated);
      }
      if (addDoctorContext === "receveur" && form.hopitalDestination) {
        const updated = await fetchMedecinsByHopital(form.hopitalDestination);
        setMedecinsHopitalDestination(updated);
      }

      if (createdDoctor?.id) {
        if (addDoctorContext === "demandeur") {
          handleChange("medecinDemandeur", createdDoctor.id);
        } else if (addDoctorContext === "receveur") {
          handleChange("medecinReceveur", createdDoctor.id);
        }
      }

      toast({
        title: "Médecin ajouté",
        status: "success",
        isClosable: true,
      });

      setIsAddDoctorModalOpen(false);
      setAddDoctorContext(null);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter le médecin.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setCreatingDoctor(false);
    }
  };

  return (
    <Box pt={{ base: "200px", md: "160px", xl: "160px" }}>
      <Box bg="white" border="1px solid #E5E5E5" borderRadius="12px" p="16px">
        <Grid templateColumns={{ base: "1fr", xl: "2.2fr 1fr" }} gap="16px" alignItems="start">
          <GridItem>
            <VStack align="stretch" spacing="16px">
              <Text fontSize="16px" fontWeight="700" color="#1B2559">
                Créer dossier de régulation
              </Text>

              <Box border="1px solid #ECECEC" borderRadius="10px" p="12px">
                <Text fontSize="12px" color="gray.500" mb="8px">
                  Transport
                </Text>
                <RadioGroup
                  value={form.transportType}
                  onChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      transportType: value,
                      accordDestination: "",
                      medecinReceveur: "",
                    }))
                  }
                >
                  <HStack spacing="24px">
                    <Radio value="primaire">Primaire</Radio>
                    <Radio value="secondaire">Secondaire</Radio>
                  </HStack>
                </RadioGroup>
              </Box>

              <Box border="1px solid #ECECEC" borderRadius="10px" p="12px">
                <HStack mb="10px" justify="space-between">
                  <HStack>
                    <Icon as={MdLocalHospital} color="#2B6CF0" />
                    <Text fontWeight="600" fontSize="14px">Identification Patient</Text>
                  </HStack>
                  <Icon as={MdExpandMore} color="gray.500" />
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10px">
                  <FormControl isRequired={form.transportType === "secondaire"}><FormLabel fontSize="12px">IPP</FormLabel><Input size="sm" placeholder="Numéro IPP" value={form.ipp} onChange={(e) => handleIppChange(e.target.value)} /></FormControl>
                  <FormControl isRequired><FormLabel fontSize="12px">Nom</FormLabel><Input size="sm" placeholder="Entrez le nom" value={form.nom} onChange={(e) => handleChange("nom", e.target.value)} /></FormControl>
                  <FormControl isRequired><FormLabel fontSize="12px">Prénom</FormLabel><Input size="sm" placeholder="Entrez le prénom" value={form.prenom} onChange={(e) => handleChange("prenom", e.target.value)} /></FormControl>
                  {form.transportType === "primaire" && (
                    <FormControl isRequired>
                      <FormLabel fontSize="12px">Téléphone patient</FormLabel>
                      <Input size="sm" value={form.telephonePatient} onChange={(e) => handleChange("telephonePatient", e.target.value)} placeholder="6 75 87 90 67" />
                    </FormControl>
                  )}
                  <FormControl isRequired>
                    <FormLabel fontSize="12px">Âge</FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      min={0}
                      step={1}
                      placeholder="Entrez l'âge"
                      value={form.age}
                      onChange={(e) => handleChange("age", e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="12px">Sexe</FormLabel>
                    <RadioGroup value={form.sexe} onChange={(value) => handleChange("sexe", value)}>
                      <HStack><Radio value="femme">Femme</Radio><Radio value="homme">Homme</Radio></HStack>
                    </RadioGroup>
                  </FormControl>
                </SimpleGrid>

                {form.transportType !== "secondaire" && (
                  <>
                    <Text fontSize="11px" color="gray.500" mt="12px" mb="6px">Appelant</Text>
                    <Checkbox
                      size="sm"
                      isChecked={form.appelantEstPatient}
                      onChange={(e) => handleChange("appelantEstPatient", e.target.checked)}
                      mb="10px"
                    >
                      <Text fontSize="12px">L'appelant est le patient</Text>
                    </Checkbox>

                    {!form.appelantEstPatient && (
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10px">
                        <FormControl><FormLabel fontSize="12px">Nom appelant</FormLabel><Input size="sm" placeholder="Entrez le nom nom appelant" value={form.nomAppelant} onChange={(e) => handleChange("nomAppelant", e.target.value)} /></FormControl>
                        <FormControl><FormLabel fontSize="12px">Relation avec le patient</FormLabel><Select size="sm" {...missionFieldProps} value={form.relationAppelant} onChange={(e) => handleChange("relationAppelant", e.target.value)}><option value="">Sélectionner...</option><option>Parent</option><option>Conjoint</option><option>Autre</option></Select></FormControl>
                        <FormControl>
                          <FormLabel fontSize="12px">Téléphone appelant</FormLabel>
                          <Input size="sm" value={form.telephoneAppelant} onChange={(e) => handleChange("telephoneAppelant", e.target.value)} placeholder="6 75 87 90 67" />
                        </FormControl>
                      </SimpleGrid>
                    )}
                  </>
                )}
              </Box>

              <Box border="1px solid #ECECEC" borderRadius="10px" p="12px">
                <HStack mb="10px" justify="space-between">
                  <HStack>
                    <Icon as={MdOutlineWarningAmber} color="#2B6CF0" />
                    <Text fontWeight="600" fontSize="14px">Motif de transfert</Text>
                  </HStack>
                  <Icon as={MdExpandMore} color="gray.500" />
                </HStack>
                {form.transportType === "secondaire" ? (
                  <VStack align="stretch" spacing="10px" w="100%">
                    <FormControl>
                      <FormLabel fontSize="12px">Observation brève</FormLabel>
                      <Textarea
                        size="sm"
                        rows={3}
                        borderRadius="6px"
                        border="1px solid"
                        borderColor="#E5E5E5"
                        fontSize="13px"
                        placeholder="Synthèse clinique courte..."
                        value={form.observationBreveTransfert}
                        onChange={(e) => handleChange("observationBreveTransfert", e.target.value)}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="12px">Motif de transfert</FormLabel>
                      <Input
                        size="sm"
                        placeholder="Motif du transfert..."
                        value={form.motif}
                        onChange={(e) => handleChange("motif", e.target.value)}
                      />
                    </FormControl>
                  </VStack>
                ) : (
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10px">
                    <FormControl isRequired>
                      <FormLabel fontSize="12px">Motif de transfert</FormLabel>
                      <Input size="sm" placeholder="Décrire brièvement le motif..." value={form.motif} onChange={(e) => handleChange("motif", e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="12px">Circonstances</FormLabel>
                      <Input size="sm" placeholder="Lieu, heure, contexte..." value={form.circonstances} onChange={(e) => handleChange("circonstances", e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="12px">Gestes déjà effectués</FormLabel>
                      <Input size="sm" placeholder="O2, VVP, Médicaments..." value={form.gestes} onChange={(e) => handleChange("gestes", e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="12px">Danger sur les lieux</FormLabel>
                      <Select size="sm" {...missionFieldProps} value={form.danger} onChange={(e) => handleChange("danger", e.target.value)}>
                        <option value="">Sélectionnez la ville...</option>
                        <option>Oui</option>
                        <option>Non</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="12px">Victimes</FormLabel>
                      <HStack border="1px solid #E2E8F0" borderRadius="8px" w="90px" h="32px" justify="space-between" px="8px">
                        <Button
                          size="xs"
                          variant="ghost"
                          minW="20px"
                          h="20px"
                          onClick={() => handleChange("victimes", String(Math.max(1, Number(form.victimes || 1) - 1)))}
                        >
                          -
                        </Button>
                        <Text fontSize="12px" fontWeight="600">{form.victimes}</Text>
                        <Button
                          size="xs"
                          variant="ghost"
                          minW="20px"
                          h="20px"
                          onClick={() => handleChange("victimes", String(Number(form.victimes || 1) + 1))}
                        >
                          +
                        </Button>
                      </HStack>
                    </FormControl>
                  </SimpleGrid>
                )}
              </Box>

              <Box border="1px solid #ECECEC" borderRadius="10px" p="12px">
                <HStack mb="10px" justify="space-between">
                  <HStack spacing="8px">
                    <Icon as={MdMonitorHeart} color="#2B6CF0" />
                    <Text fontWeight="600" fontSize="14px">Constantes</Text>
                  </HStack>
                  <Icon as={MdExpandMore} color="gray.500" />
                </HStack>

                {form.transportType !== "secondaire" && (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="10px" mb="12px">
                    <FormControl isRequired>
                      <FormLabel fontSize="12px">Respire</FormLabel>
                      <RadioGroup value={form.respire} onChange={(value) => handleChange("respire", value)}>
                        <HStack spacing="20px">
                          <Radio value="oui">Oui</Radio>
                          <Radio value="non">Non</Radio>
                        </HStack>
                      </RadioGroup>
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="12px">Conscient</FormLabel>
                      <RadioGroup value={form.conscient} onChange={(value) => handleChange("conscient", value)}>
                        <HStack spacing="20px">
                          <Radio value="oui">Oui</Radio>
                          <Radio value="non">Non</Radio>
                        </HStack>
                      </RadioGroup>
                    </FormControl>
                  </SimpleGrid>
                )}

                {form.transportType !== "secondaire" && (
                  <HStack spacing="10px" align="center" mb="10px">
                    <Text fontSize="13px" color="gray.500" whiteSpace="nowrap">
                      Constantes
                    </Text>
                    <Divider />
                  </HStack>
                )}

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
                  <Box border="1px solid #E6E8EC" borderRadius="10px" p="10px">
                    <Text textAlign="center" fontSize="13px" fontWeight="600" mb="8px">Tension Artérielle</Text>
                    <HStack border="1px solid #E5E7EB" borderRadius="8px" h="36px" px="10px" justify="space-between">
                      <Button size="xs" variant="ghost" minW="20px" h="20px" onClick={() => handleIncrementTension(-1)}>-</Button>
                      <Input
                        value={form.tension}
                        onChange={(e) => handleChange("tension", e.target.value)}
                        size="xs"
                        h="22px"
                        border="none"
                        textAlign="center"
                        fontWeight="600"
                        color="gray.600"
                        _focus={{ boxShadow: "none" }}
                      />
                      <Button size="xs" variant="ghost" minW="20px" h="20px" onClick={() => handleIncrementTension(1)}>+</Button>
                    </HStack>
                    <Text textAlign="center" fontSize="12px" color="gray.500" mt="6px">mmHg</Text>
                  </Box>

                  <Box border="1px solid #E6E8EC" borderRadius="10px" p="10px">
                    <Text textAlign="center" fontSize="13px" fontWeight="600" mb="8px">Fréquence Cardiaque</Text>
                    <HStack border="1px solid #E5E7EB" borderRadius="8px" h="36px" px="10px" justify="space-between">
                      <Button size="xs" variant="ghost" minW="20px" h="20px" onClick={() => handleIncrementField("frequenceCardiaque", -1)}>-</Button>
                      <Input
                        value={form.frequenceCardiaque}
                        onChange={(e) => handleChange("frequenceCardiaque", e.target.value)}
                        size="xs"
                        h="22px"
                        border="none"
                        textAlign="center"
                        fontWeight="600"
                        color="gray.600"
                        _focus={{ boxShadow: "none" }}
                      />
                      <Button size="xs" variant="ghost" minW="20px" h="20px" onClick={() => handleIncrementField("frequenceCardiaque", 1)}>+</Button>
                    </HStack>
                    <Text textAlign="center" fontSize="12px" color="gray.500" mt="6px">bpm</Text>
                  </Box>

                  <Box border="1px solid #E6E8EC" borderRadius="10px" p="10px">
                    <Text textAlign="center" fontSize="13px" fontWeight="600" mb="8px">SpO2</Text>
                    <HStack border="1px solid #E5E7EB" borderRadius="8px" h="36px" px="10px" justify="space-between">
                      <Button size="xs" variant="ghost" minW="20px" h="20px" onClick={() => handleIncrementField("spo2", -1)}>-</Button>
                      <Input
                        value={form.spo2}
                        onChange={(e) => handleChange("spo2", e.target.value)}
                        size="xs"
                        h="22px"
                        border="none"
                        textAlign="center"
                        fontWeight="600"
                        color="gray.600"
                        _focus={{ boxShadow: "none" }}
                      />
                      <Button size="xs" variant="ghost" minW="20px" h="20px" onClick={() => handleIncrementField("spo2", 1)}>+</Button>
                    </HStack>
                    <Text textAlign="center" fontSize="12px" color="gray.500" mt="6px">%</Text>
                  </Box>

                  <Box border="1px solid #E6E8EC" borderRadius="10px" p="10px">
                    <Text textAlign="center" fontSize="13px" fontWeight="600" mb="8px">Score de glasgow</Text>
                    <HStack border="1px solid #E5E7EB" borderRadius="8px" h="36px" px="10px" justify="space-between">
                      <Button size="xs" variant="ghost" minW="20px" h="20px" onClick={() => handleIncrementField("glasgow", -1)}>-</Button>
                      <Input
                        value={form.glasgow}
                        onChange={(e) => handleChange("glasgow", e.target.value)}
                        size="xs"
                        h="22px"
                        border="none"
                        textAlign="center"
                        fontWeight="600"
                        color="gray.600"
                        _focus={{ boxShadow: "none" }}
                      />
                      <Button size="xs" variant="ghost" minW="20px" h="20px" onClick={() => handleIncrementField("glasgow", 1)}>+</Button>
                    </HStack>
                    <Text textAlign="center" fontSize="12px" color="gray.500" mt="6px">/15</Text>
                  </Box>
                </SimpleGrid>

                <FormControl mt="12px">
                  <FormLabel fontSize="12px">Autres constantes / Remarques</FormLabel>
                  <Input
                    size="sm"
                    {...missionFieldProps}
                    value={form.autresConstantes}
                    onChange={(e) => handleChange("autresConstantes", e.target.value)}
                    placeholder="Température, glycémie, douleur (EVA), etc."
                  />
                </FormControl>

              </Box>

              <Box border="1px solid #ECECEC" borderRadius="10px" p="12px">
                <HStack mb="10px" justify="space-between">
                  <HStack spacing="8px">
                    <Icon as={MdLocationOn} color="#2B6CF0" />
                    <Text fontWeight="600" fontSize="14px">
                      {form.transportType === "primaire" ? "Transport " : "Transport "}
                    </Text>
                  </HStack>
                  <Icon as={MdExpandMore} color="gray.500" />
                </HStack>
                {form.transportType === "secondaire" ? (
                  <>
                    <HStack spacing="10px" align="center" mb="10px">
                      <Text fontSize="13px" color="gray.500" whiteSpace="nowrap">
                        Origine (Demandeur)
                      </Text>
                      <Divider />
                    </HStack>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10px">
                      <FormControl isRequired>
                        <FormLabel fontSize="12px">Ville mission</FormLabel>
                        <VilleSearchDropdown
                          value={form.villeDemandeur}
                          onChange={(ville) => {
                            handleChange("villeDemandeur", ville);
                            handleChange("hopitalDemandeur", "");
                            handleChange("medecinDemandeur", "");
                          }}
                          options={hospitalVillesList}
                          placeholder="Sélectionner..."
                        />
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel fontSize="12px">Hôpital / Adresse mission</FormLabel>
                        <HospitalSearchDropdown
                          value={form.hopitalDemandeur}
                          onChange={(hospitalId) => {
                            handleChange("hopitalDemandeur", hospitalId);
                            handleChange("medecinDemandeur", "");
                          }}
                          options={hospitals.filter((h) => !form.villeDemandeur || String(h.ville || "").trim() === String(form.villeDemandeur || "").trim())}
                          placeholder="Sélectionner..."
                        />
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel fontSize="12px">Service</FormLabel>
                        <Input size="sm" {...missionFieldProps} placeholder="Saisissez le service..." value={form.serviceDemandeur} onChange={(e) => handleChange("serviceDemandeur", e.target.value)} />
                      </FormControl>
                      <FormControl isRequired>
                        <Flex justify="space-between" align="center" mb="6px" gap="10px">
                          <FormLabel fontSize="12px" mb="0">Médecin demandeur</FormLabel>
                          <Button size="xs" variant="outline" onClick={() => openAddDoctorModal("demandeur")} isDisabled={!form.hopitalDemandeur}>
                            Ajouter un médecin
                          </Button>
                        </Flex>
                        <DoctorSearchDropdown
                          value={form.medecinDemandeur}
                          onChange={(doctorId) => handleChange("medecinDemandeur", doctorId)}
                          options={medecinsHopitalMission}
                          placeholder="Recherche Dr."
                        />
                      </FormControl>
                    </SimpleGrid>
                  </>
                ) : (
                  <>
                    <HStack spacing="10px" align="center" mb="10px">
                      <Text fontSize="13px" color="gray.500" whiteSpace="nowrap">
                        Origine
                      </Text>
                      <Divider />
                    </HStack>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="10px">
                      <FormControl isRequired>
                        <FormLabel fontSize="12px">Ville mission</FormLabel>
                        <VilleSearchDropdown
                          value={form.villeMissionPrimaire}
                          onChange={(ville) => handleChange("villeMissionPrimaire", ville)}
                          options={hospitalVillesList}
                          placeholder="Sélectionner..."
                        />
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel fontSize="12px">Adresse mission</FormLabel>
                        {googleMapsLoaded ? (
                          <AddressAutocomplete
                            value={form.adresseOriginePrimaire}
                            onChange={(value) => handleChange("adresseOriginePrimaire", value)}
                            placeholder="Entrez l'adresse d'origine"
                            onLocationSelect={(addressData) =>
                              setPrimaryLocation(addressData?.location || null)
                            }
                          />
                        ) : (
                          <Input
                            size="sm"
                            {...missionFieldProps}
                            placeholder="Entrez l'adresse d'origine"
                            value={form.adresseOriginePrimaire}
                            onChange={(e) => handleChange("adresseOriginePrimaire", e.target.value)}
                          />
                        )}
                      </FormControl>
                      
                    </SimpleGrid>
                  </>
                )}

                <HStack spacing="10px" align="center" mb="10px" mt="12px">
                  <Text fontSize="13px" color="gray.500" whiteSpace="nowrap">
                    Destination
                  </Text>
                  <Divider />
                </HStack>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10px">
                  <FormControl>
                    <FormLabel fontSize="12px">Ville destination</FormLabel>
                    <VilleSearchDropdown
                      value={form.villeDestination}
                      onChange={(ville) => {
                        handleChange("villeDestination", ville);
                        handleChange("hopitalDestination", "");
                        handleChange("medecinReceveur", "");
                        handleChange("accordDestination", "");
                      }}
                      options={hospitalVillesList}
                      placeholder="Sélectionner..."
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="12px">Hôpital / Adresse destination</FormLabel>
                    <HospitalSearchDropdown
                      value={form.hopitalDestination}
                      onChange={(hospitalId) => {
                        handleChange("hopitalDestination", hospitalId);
                        handleChange("medecinReceveur", "");
                      }}
                      options={hospitals.filter((h) => !form.villeDestination || String(h.ville || "").trim() === String(form.villeDestination || "").trim())}
                      placeholder="Sélectionner..."
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="12px">Service destination</FormLabel>
                    <Input size="sm" {...missionFieldProps} placeholder="Saisissez le service destination..." value={form.serviceDestination} onChange={(e) => handleChange("serviceDestination", e.target.value)} />
                  </FormControl>
                </SimpleGrid>

                <FormControl mt="12px">
                  <FormLabel fontSize="12px">Accord obtenu</FormLabel>
                  <RadioGroup
                    value={form.accordDestination}
                    onChange={(v) => {
                      handleChange("accordDestination", v);
                      if (v !== "oui") {
                        handleChange("medecinReceveur", "");
                      }
                    }}
                  >
                    <HStack spacing="24px">
                      <Radio value="oui">Oui</Radio>
                      <Radio value="non">Non</Radio>
                    </HStack>
                  </RadioGroup>
                </FormControl>

                {form.accordDestination === "oui" && (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="10px" mt="10px">
                    <FormControl isRequired>
                      <Flex justify="space-between" align="center" mb="6px" gap="10px">
                        <FormLabel fontSize="12px" mb="0">Médecin receveur</FormLabel>
                        <Button size="xs" variant="outline" onClick={() => openAddDoctorModal("receveur")} isDisabled={!form.hopitalDestination}>
                          Ajouter un médecin
                        </Button>
                      </Flex>
                      <DoctorSearchDropdown
                        value={form.medecinReceveur}
                        onChange={(doctorId) => selectMedecinReceveur(doctorId)}
                        options={medecinsHopitalDestination}
                        placeholder="Recherche Dr."
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="12px">N° médecin receveur</FormLabel>
                      <Box
                        minH="42px"
                        px="12px"
                        py="10px"
                        border="1px solid"
                        borderColor="#E5E5E5"
                        borderRadius="6px"
                        bg="gray.50"
                      >
                        <Text fontSize="13px" color={medecinReceveurTelephoneLu ? "gray.800" : "gray.500"}>
                          {medecinReceveurTelephoneLu || "—"}
                        </Text>
                      </Box>
                    </FormControl>
                  </SimpleGrid>
                )}
              </Box>
            </VStack>
          </GridItem>

          <GridItem borderLeft={{ base: "none", xl: "1px solid #ECECEC" }} pl={{ base: 0, xl: "12px" }}>
            <VStack align="stretch" spacing="12px" position="sticky" top="170px">
              <HStack justify="space-between">
                <HStack spacing="8px">
                  <Icon as={MdMonitorHeart} color="#2B6CF0" />
                  <Text fontSize="16px" fontWeight="700" color="#111827">
                    Triage
                  </Text>
                </HStack>
                <Icon as={MdExpandMore} color="gray.500" />
              </HStack>

              <HStack spacing="10px" align="center">
                <Text fontSize="13px" color="gray.500" whiteSpace="nowrap">
                  Niveau d'Urgence
                </Text>
                <Divider />
              </HStack>

              <Text fontSize="15px" fontWeight="700" color="#111827">
                Niveau d'urgence <Text as="span" color="red.500">*</Text>
              </Text>
              <SimpleGrid columns={2} spacing="8px">
                {[
                  { code: "U0", label: "Réanimation immédiate", color: "#D83535" },
                  { code: "U1", label: "Urgence vitale", color: "#D83535" },
                  { code: "U2", label: "Urgence relative", color: "#F97316" },
                  { code: "U3", label: "Pas d'urgence identifiée", color: "#F97316" },
                  { code: "U4", label: "Réservation SMUR", color: "#008044" },
                  { code: "U5", label: "Conseil médical", color: "#008044" },
                ].map((level) => (
                  <Button
                    key={level.code}
                    h="66px"
                    borderRadius="8px"
                    bg={form.priorite === level.code.toLowerCase() ? "#EFF6FF" : "white"}
                    border="1px solid"
                    borderColor={form.priorite === level.code.toLowerCase() ? "rgba(69,156,233,0.35)" : "#E5E7EB"}
                    _hover={{ bg: form.priorite === level.code.toLowerCase() ? "#EFF6FF" : "#F9FAFB" }}
                    onClick={() => handleChange("priorite", level.code.toLowerCase())}
                  >
                    <VStack spacing="0">
                      <Text fontSize="18px" fontWeight="700" color={level.color} lineHeight="1">
                        {level.code}
                      </Text>
                      <Text fontSize="12px" color="#6B7280" lineHeight="1.1" textAlign="center">
                        {level.label}
                      </Text>
                    </VStack>
                  </Button>
                ))}
              </SimpleGrid>
              <FormControl isRequired>
                <FormLabel fontSize="14px" fontWeight="600">
                  Notes complémentaires pour le médecin régulateur
                </FormLabel>
                <Textarea
                  size="sm"
                  value={form.notesTriage}
                  onChange={(e) => handleChange("notesTriage", e.target.value)}
                  placeholder="Éléments importants pour la régulation médicale..."
                />
              </FormControl>
            </VStack>
          </GridItem>
        </Grid>

        <Divider my="12px" />
        <HStack justify="space-between">
          <Button
            variant="ghost"
            size="sm"
            fontSize="13px"
            fontWeight="500"
            color="#111827"
            onClick={() => navigate("/admin/dossier-regulation")}
          >
            Annuler dossier
          </Button>
          <HStack spacing="8px">
            {isCriticalPriority ? (
              <Button
                size="sm"
                h="32px"
                px="14px"
                bg="#C53030"
                color="white"
                fontSize="12px"
                fontWeight="600"
                leftIcon={<Icon as={MdAdd} boxSize="14px" />}
                _hover={{ bg: "#B91C1C" }}
                onClick={handleCreateDossier}
                isLoading={isSubmittingDossier}
              >
                Créer mission SMUR
              </Button>
            ) : (
              <Button
                size="sm"
                h="32px"
                px="14px"
                bg="#047857"
                color="white"
                fontSize="12px"
                fontWeight="600"
                leftIcon={<Icon as={MdPhoneForwarded} boxSize="14px" />}
                _hover={{ bg: "#065F46" }}
                onClick={handleCreateDossier}
                isLoading={isSubmittingDossier}
              >
                Transférer au médecin
              </Button>
            )}
          </HStack>
        </HStack>
      </Box>
      <Modal
        isOpen={isAffectationModalOpen}
        onClose={closeAffectationModal}
        size="6xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxW="1200px">
          <ModalHeader>
            Affecter la mission - Dossier #{createdUrgentDossier?.id || "-"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p="16px">
            {!missionLocation ? (
              <Box border="1px solid" borderColor="gray.200" borderRadius="10px" p="16px" bg="gray.50">
                <Text color="red.500" fontSize="14px">
                  Position du dossier indisponible. Vérifiez le champ `loc` de ce dossier.
                </Text>
              </Box>
            ) : (
              <AffectationMap
                vehicles={vehicles}
                missionLocation={missionLocation}
                selectedAmbulanceId={selectedAmbulanceId}
                onAmbulanceSelect={setSelectedAmbulanceId}
                doctors={[]}
                ambulanciers={allAmbulanciers}
                fetchVehicleLocation={fetchVehicleLocation}
                nurses={[]}
                selectedDoctorId=""
                selectedNurseId=""
                onDoctorSelect={() => {}}
                onNurseSelect={() => {}}
                typeMission="secondaire"
                typeAmbulance=""
                containerHeight="78vh"
              />
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing="10px">
              <Button variant="outline" onClick={closeAffectationModal}>
                Annuler
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleCreateMissionFromCreatedDossier}
                isLoading={assigningMission}
                isDisabled={!missionLocation}
              >
                Créer la mission
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isAddDoctorModalOpen}
        onClose={() => {
          if (!creatingDoctor) {
            setIsAddDoctorModalOpen(false);
            setAddDoctorContext(null);
          }
        }}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Ajouter un médecin</ModalHeader>
          <ModalCloseButton disabled={creatingDoctor} />
          <ModalBody pb={4}>
            <VStack spacing="10px" align="stretch">
              <FormControl isRequired>
                <FormLabel>Nom</FormLabel>
                <Input
                  value={newDoctorData.nom}
                  onChange={(e) => setNewDoctorData((prev) => ({ ...prev, nom: e.target.value }))}
                  placeholder="Nom du médecin"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Prénom</FormLabel>
                <Input
                  value={newDoctorData.prenom}
                  onChange={(e) => setNewDoctorData((prev) => ({ ...prev, prenom: e.target.value }))}
                  placeholder="Prénom du médecin"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Téléphone</FormLabel>
                <Input
                  value={newDoctorData.téléphone}
                  onChange={(e) => setNewDoctorData((prev) => ({ ...prev, téléphone: e.target.value }))}
                  placeholder="Téléphone"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Ville</FormLabel>
                <Input
                  value={newDoctorData.ville}
                  onChange={(e) => setNewDoctorData((prev) => ({ ...prev, ville: e.target.value }))}
                  placeholder="Ville"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Spécialité</FormLabel>
                <Select
                  size="sm"
                  h="40px"
                  borderRadius="6px"
                  value={newDoctorData.specialite}
                  onChange={(e) => setNewDoctorData((prev) => ({ ...prev, specialite: e.target.value }))}
                >
                  <option value="">Sélectionner une spécialité</option>
                  {MEDECIN_SPECIALITE_OPTIONS.map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={() => setIsAddDoctorModalOpen(false)} isDisabled={creatingDoctor}>
              Annuler
            </Button>
            <Button colorScheme="blue" onClick={handleCreateDoctor} isLoading={creatingDoctor}>
              Enregistrer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
